<template>
    <ai-popup
        title="Список ролей приложения. Выбор ролей для пользователя"
        :bottom-buttons-enabled="true"
        first-button-text="Выбрать"
        width="60%"
        height="75%"
        v-model:visible="state.usersRolePopupVisible"
        @submit-click="showSelectedRows"
        @close-popup="closeClick"
    >
        <local-grid ref="allRolesRef" :meta="meta" :data-source="roles" :selected-row-keys="preselectedRows">
            <template #toolbarItems>
                <dx-item location="before">
                    <dx-tag-box
                        v-model:value="selectedApps"
                        :items="environmentState.allAppList"
                        :show-selection-controls="true"
                        :max-displayed-tags="2"
                        :multiline="false"
                        value-expr="name_app"
                        display-expr="name_app"
                        width="400"
                        @valueChanged="filterRolesByApp"
                    />
                </dx-item>
            </template>
            <template #restrictions="{ data }">
                <div class="flex items-center justify-between">
                    <div class="w-[92%] mr-2">
                        {{ data.data.description }}
                    </div>
                    <div class="justify-left">
                        <dx-button
                            :icon="AiEllipsis"
                            class="button-ellipsis cursor-pointer"
                            width="20px"
                            hint="Просмотр областей и типов доступа в роли"
                            @click="showPopup(data)"
                        />
                    </div>
                </div>
            </template>
        </local-grid>
    </ai-popup>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { DxButton, DxTagBox } from 'devextreme-vue';
import { DxItem } from 'devextreme-vue/data-grid';
import LocalGrid from '@/components/local-grid.vue';
import AiPopup from '@/components/ai-popup/ai-popup.vue';
import usersLocalStore from '@/views/administration/users/store.js';
import environmentStore from '@/store/enviroment-data.js';
import rolesStore from '@/store/administration/roles.js';
import { getCookie } from '@/utils/cookies.js';
import AiEllipsis from '@/assets/ai-ellipsis.svg';

const { state, loadById } = usersLocalStore;
const { state: environmentState, getAllAppList } = environmentStore;
const { rolesState, getRoleList, getAreaList, getGroupList, getAreaListWithApps } = rolesStore;

const allRolesRef = ref();
const selectedItemKeys = ref(true);
const gridInstance = ref(null);
const selectedItems = ref([]);
const roles = ref([]);

const app = ref(+getCookie('app'));
const selectedApps = ref();

const emit = defineEmits(['items-event']);
const preselectedRows = ref([]);

onMounted(async () => {
    if (environmentState.allAppList.length < 1) await getAllAppList();
    gridInstance.value = allRolesRef.value.gridInstance;
    gridInstance.value.on('selectionChanged', onSelectionChanged);
    await getRoleList();
    state.isOpenUserCard = true;
    state.isOpenInsertAreas = false;
    roles.value = buildRoles();
    const currentAppName = { value: [environmentState.allAppList.find(application => application.kod_app === app.value).name_app] };
    selectedApps.value = currentAppName.value;
    filterRolesByApp(currentAppName);
    gridInstance.value.deselectAll();
    preselectedRows.value = state.userRequestBody.roles?.map(role => role.id) ?? [];
});

const buildRoles = () => {
    return rolesState.roleList.map(role => ({
        ...role,
        areas_names: role.areas?.map(area => area.name).join(', '),
        apps_names: getAppNamesByIds(role.areas?.map(area => area.app_id)),
    }));
};

const getAppNamesByIds = appIds => {
    return environmentState.allAppList
        .filter(app => appIds?.includes(app.kod_app))
        .map(app => app.name_app)
        .join(', ');
};

const filterRolesByApp = (event) => {
    const incomingFilterValue = event.value;
    const filterValueForGrid = event.value.length
        ? [
            ...incomingFilterValue.map(val => ['apps_names', 'contains', val]),
            'or',
        ]
        : null;
    gridInstance.value.option('filterValue', filterValueForGrid);
};

const showPopup = async event => {
    await getAreaList();
    await getAreaListWithApps();
    await getGroupList();

    const clickedRoleId = event.data.id;
    const clickedRole = roles.value.find(role => role.id === clickedRoleId);
    const clickedAppId = clickedRole ? clickedRole.areas[0].app_id : app.value;

    await loadById(clickedRoleId, clickedAppId);
    state.isClosingConfirmed = true;
    state.isOpenUserCard = false;
    state.isOpenInsertAreas = true;
    state.roleAreasPopupVisible = true;
};

const closeClick = () => {
    state.userCardPopupVisible = true;
};

const onSelectionChanged = event => {
    if (!event.selectedRowKeys.length) return;
    selectedItemKeys.value = event.selectedRowKeys.length <= 0;
    selectedItems.value = event.selectedRowsData;
};

const showSelectedRows = () => {
    emit('items-event', selectedItems.value);
    state.isOpenUserCard = true;
    state.usersRolePopupVisible = false;
    state.userCardPopupVisible = !state.isOpenInsertAreas;
    gridInstance.value.deselectAll();
};

const meta = {
    keyExpr: 'id',
    wordWrapEnabled: true,
    allowSelectAll: true,
    scrollingMode: 'standart',
    columns: [
        {
            dataField: 'id',
            caption: 'ID',
            visible: false,
            dataType: 'number',
        },
        {
            dataField: 'code',
            caption: 'Код роли',
            dataType: 'string',
            sortOrder: 'asc',
            width: 200,
        },
        {
            dataField: 'name',
            caption: 'Роль',
            dataType: 'string',
        },
        {
            dataField: 'areas',
            caption: 'Области роли',
            dataType: 'string',
            cellTemplate: 'restrictions',
            allowHeaderFiltering: false,
        },
        {
            dataField: 'apps_names',
            caption: 'Приложения',
            dataType: 'string',
        },
    ],
};
</script>

<style lang="scss" scoped>
:deep(.button-ellipsis.dx-button) {
    min-width: 0;
    min-height: 0;
    height: unset;
    display: flex;

    .dx-button-content {
        padding: 0;
        min-width: 0;
        min-height: 0;
    }

    .dx-icon {
        width: 20px;
        height: 20px;
        min-width: 0;
    }
}
</style>
